import {
  createConversation,
  regenerateConversationMessage,
  updateConversation,
} from 'api/v2/AI';
import { useAiMetadata } from 'contexts/AIMetadataProvider';
import { last } from 'lodash';
import type { FC } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router';
import ReactSpeechRecognition from 'react-speech-recognition';
import type {
  ConversationMessagePart,
  ConversationMetadata,
  ConversationQuery,
  Message,
  StreamingChunk,
} from 'types/ai.types';
import {
  AIAgentEnum,
  AIRoleEnum,
  type AIUsage,
  type ConversationHistory,
  type ConversationMessage,
} from 'types/ai.types';
import { AICacheKeys } from 'types/cacheKeys.types';
import { logAmplitude } from 'utils/amplitude';
import {
  WEB_AIASSISTANT_NEWCHAT,
  WEB_AIASSISTANT_USERSENTMESSAGE,
} from 'utils/amplitude/events/ai.amplitude';
import { ONE_SECOND } from 'utils/time';
import type { AIChatContextValue, AIChatProviderProps } from './AIChat.types';
import {
  useAIChatAttachment,
  useConversationMetadata,
  useConversationQuery,
  useGetGratificationById,
  useUsageQuery,
} from './hooks';
import { scrollToBottom } from './utils';
import showToast from 'utils/toast';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

export const AIChatContext = createContext<AIChatContextValue>(
  {} as AIChatContextValue,
);

export const AIChatProvider: FC<AIChatProviderProps> = ({
  children,
  aiAgent = AIAgentEnum.default,
  allowFileMetadata = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoPrompt, setIsAutoPrompt] = useState(false);
  const [message, setMessage] = useState('');
  const [currentTipId, setCurrentTipId] = useState('');
  const [streamedData, setStreamedData] = useState<ConversationMessagePart[]>(
    [],
  );
  const [conversationId, setConversationId] = useState('');
  const [conversation, setConversation] = useState<ConversationHistory | null>(
    null,
  );

  const { metadata, setMetadata } = useAiMetadata();

  const location = useLocation();

  const { data: gratification } = useGetGratificationById(currentTipId);
  const abortController = useRef(new AbortController());

  const { refetch: refetchUsage, data: usage = {} as AIUsage } = useUsageQuery(
    conversation,
    message,
  );

  const { metadataItems, refetchMetadata } = useConversationMetadata(
    conversationId,
    metadata,
  );

  const { t } = useTranslation();

  const isStreaming = useMemo(() => !!streamedData.length, [streamedData]);

  const allMessages: Message[] = useMemo(() => {
    const messages = conversation?.messages || [];
    const streamedMessage = streamedData.length
      ? {
          _id: `streaming_message`,
          role: AIRoleEnum.assistant,
          parts: streamedData,
          content: '',
        }
      : null;

    return streamedMessage ? [...messages, streamedMessage] : messages;
  }, [conversation?.messages, streamedData]);

  const metadataItemsMap = useMemo(() => {
    if (!metadataItems) return {};

    return metadataItems.reduce((acc, item) => {
      if (item._id) acc[item._id] = item;
      return acc;
    }, {} as Record<string, ConversationMetadata>);
  }, [metadataItems]);

  const queryClient = useQueryClient();
  const attachmentApi = useAIChatAttachment(allowFileMetadata);

  const { refetch: refetchConversation } = useConversationQuery(
    conversationId,
    (data) => {
      scrollToBottom();
      setConversation(data);
      setConversationId(data?._id || '');
      setStreamedData([]);

      attachmentApi.remove();
      if (conversationId) {
        refetchUsage();
      }
    },
  );

  useEffect(() => {
    setCurrentTipId('');
    setConversation(null);
  }, [location.pathname]);

  const onDownloadProgress = (progress: ProgressEvent): void => {
    const xhr = progress?.target as XMLHttpRequest | null;
    const buffer = (xhr?.response as string | undefined) || '';
    const chunks = buffer.split('\n') as string[];
    chunks.pop();

    const lastLine = last(chunks);
    if (!lastLine) return;

    const lastChunk: StreamingChunk = JSON.parse(lastLine);

    if (
      lastChunk?.finish_reason ||
      (lastChunk?.parts?.length === 1 &&
        lastChunk?.parts[0]?.type === 'consent_text')
    ) {
      // Parse the first chunk to get conversation ID
      if (conversationId !== lastChunk?.conversationId) {
        setConversationId(lastChunk?.conversationId || '');
      } else {
        refetchConversation();
      }

      if (lastChunk?.finish_reason === 'error') {
        showToast(
          { title: t('backend.errors.unexpected_error') },
          { type: 'error' },
        );
      }

      return;
    }

    setStreamedData((storedParts) => {
      const incomingParts = lastChunk?.parts || [];
      if (incomingParts.length === 0) return storedParts;

      // Don't mutate the original array - work with copies
      const currentParts = [...storedParts];
      const lastStoredPart = currentParts[currentParts.length - 1];

      // Process each incoming part and accumulate properly
      // This ensures that we preserve all parts (thinking, text, tool, etc.)
      // while still concatenating consecutive parts of the same type
      const newParts = [...currentParts];

      for (let i = 0; i < incomingParts.length; i++) {
        const incomingPart = incomingParts[i];
        const isFirstIncomingPart = i === 0;

        // Only concatenate the first incoming part with the last stored part
        // if they are the same type and both support content concatenation
        const canConcatenate =
          isFirstIncomingPart &&
          lastStoredPart &&
          lastStoredPart.type === incomingPart.type &&
          (incomingPart.type === 'text' || incomingPart.type === 'thinking') &&
          'content' in lastStoredPart &&
          'content' in incomingPart;

        if (canConcatenate) {
          // Concatenate with the last stored part
          // Type narrowing: we know both parts have content at this point
          newParts[newParts.length - 1] = {
            ...lastStoredPart,
            content: lastStoredPart.content + incomingPart.content,
          };
        } else {
          // Add as a new part
          newParts.push(incomingPart);
        }
      }

      return newParts;
    });
  };

  const parseMetadata = () => {
    if (metadata) return metadata;
    if (currentTipId && gratification) {
      return {
        documentId: currentTipId,
        documentType: 'gratifications',
        content: gratification?.description || gratification?.title,
      };
    }
    return undefined;
  };

  const mutateConversation = (
    conversationMessage: Omit<ConversationMessage, '_id'>,
    id?: string,
  ) => {
    setStreamedData([]);
    logAmplitude(WEB_AIASSISTANT_USERSENTMESSAGE);
    ReactSpeechRecognition?.stopListening?.();
    abortController.current = new AbortController();
    setIsLoading(true);
    setMessage('');

    (id ? updateConversation : createConversation)(
      {
        id,
        message: conversationMessage,
        ai_agent: aiAgent !== AIAgentEnum.default ? aiAgent : undefined,
        metadata: parseMetadata(),
      } as ConversationQuery,
      {
        onDownloadProgress,
        responseType: 'stream',
        signal: abortController.current.signal,
        timeout: 180 * ONE_SECOND,
      },
    )
      .then(() => {
        if (!id) {
          queryClient?.invalidateQueries(AICacheKeys.ConversationsList);
        } else {
          refetchConversation();
          refetchMetadata();
        }
        setIsAutoPrompt(false);
        scrollToBottom();

        if (
          attachmentApi.attachment?.type === 'pdf' ||
          attachmentApi.attachment?.type === 'image'
        ) {
          attachmentApi.remove();
          setMetadata(null, attachmentApi.attachment?.type);
        }
      })
      .catch((error) => {
        showToast(
          { title: t('backend.errors.unexpected_error') },
          { type: 'error' },
        );
        Sentry.captureException(error);
      })
      .finally(() => setIsLoading(false));
  };

  const disabledSend =
    isLoading || usage?.reachedMaxCount || usage?.messageLengthExceeded;

  const handleSend = (suggestedMessage?: string) => {
    if (disabledSend) return;

    const conversationMessage: Omit<ConversationMessage, '_id'> = {
      role: AIRoleEnum.user,
      parts: [
        {
          type: 'text',
          content: suggestedMessage || message,
        },
      ],
      content: suggestedMessage || message,
    };

    if (
      attachmentApi.attachment &&
      (metadata?.documentType === 'image' || metadata?.documentType === 'pdf')
    ) {
      conversationMessage.metadataId = metadata?._id;
    }

    const newConversation = suggestedMessage
      ? {
          messages: [{ ...conversationMessage, _id: 'tempId' }],
          status: 'processing',
          user_message_count: 1,
        }
      : {
          ...(conversation || {}),
          messages: [
            ...(conversation?.messages || []),
            { ...conversationMessage, _id: 'tempId' },
          ],
          status: 'processing',
          user_message_count: 1,
        };

    setConversation(newConversation as ConversationHistory);
    attachmentApi.remove();

    scrollToBottom();

    return mutateConversation(
      conversationMessage,
      suggestedMessage ? undefined : conversation?._id,
    );
  };

  const handleRegenerateMessage = (
    messageId: string,
    parts?: ConversationMessagePart[],
  ) => {
    if (!conversation?._id) return;

    // For regeneration, we need to preserve existing parts and remove the message from conversation
    if (conversation && conversation.messages.length > 0) {
      const lastMessage =
        conversation.messages[conversation.messages.length - 1];

      // Initialize streaming with the existing parts from the message being regenerated
      setStreamedData(parts || lastMessage.parts || []);

      // Remove the last message from conversation (without mutating)
      const updatedMessages = conversation.messages.slice(0, -1);
      setConversation({
        ...conversation,
        messages: updatedMessages,
      });
    } else {
      setStreamedData([]);
    }

    abortController.current = new AbortController();
    setIsLoading(true);

    return regenerateConversationMessage(conversation._id, messageId, {
      onDownloadProgress,
      responseType: 'stream',
      signal: abortController.current.signal,
      timeout: 180 * ONE_SECOND,
    })
      .catch((error) => {
        showToast(
          { title: t('backend.errors.unexpected_error') },
          { type: 'error' },
        );
        Sentry.captureException(error);
      })
      .finally(() => setIsLoading(false));
  };

  const handleNewChat = () => {
    setCurrentTipId('');
    abortController.current.abort();
    setConversation(null);
    setConversationId('');
    setStreamedData([]);
    document.getElementById('ai-assistant-textarea')?.focus();
    logAmplitude(WEB_AIASSISTANT_NEWCHAT);
    attachmentApi.remove();
  };

  return (
    <AIChatContext.Provider
      value={{
        aiAgent,
        conversation,
        conversationId,
        currentTipId,
        isAutoPrompt,
        isLoading,
        message,
        isStreaming,
        allMessages,
        usage,
        attachmentApi,
        metadataItemsMap,
        handleNewChat,
        handleSend,
        handleRegenerateMessage,
        refetchConversation,
        setConversation,
        setConversationId,
        setCurrentTipId,
        setIsAutoPrompt,
        setMessage,
      }}
    >
      {children}
      {attachmentApi.enabled && (
        <input {...attachmentApi.dropzoneProps.input()} />
      )}
    </AIChatContext.Provider>
  );
};

export const useAIChatContext = (): AIChatContextValue => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChatContext must be used within a AIChatProvider');
  }
  return context;
};
